import type { ErrorContext } from "src/context/AnalyticsContext";
import { splitArtifactPathIntoFolderSegments } from "@common/utils/artifactFolderSegments";
import { useDioptraFetch } from "../../../hooks/use-dioptra-fetch";
import { downloadConversationArtifact } from "../../../utils/web-api";
import type { ConversationArtifact } from "@common/schemas/conversation-artifact";
import { useMemo, useState } from "react";

const buildArtifactTree = (artifacts: ConversationArtifact[]): ArtifactTreeNode => {
  const root: ArtifactTreeNode = { artifacts: [], children: new Map() };
  for (const artifact of artifacts) {
    const segments = splitArtifactPathIntoFolderSegments(artifact.path);
    let node = root;
    for (const segment of segments) {
      let child = node.children.get(segment);
      if (!child) {
        child = { artifacts: [], children: new Map() };
        node.children.set(segment, child);
      }
      node = child;
    }
    node.artifacts.push(artifact);
  }
  const sortNode = (n: ArtifactTreeNode): void => {
    n.artifacts.sort((a, b) => a.name.localeCompare(b.name));
    for (const child of n.children.values()) sortNode(child);
  };
  sortNode(root);
  return root;
};

export interface ArtifactTreeNode {
  artifacts: ConversationArtifact[];
  children: Map<string, ArtifactTreeNode>;
}

export interface UseArtifactsInputs {
  artifacts: ConversationArtifact[];
  sendError: (error: unknown, context?: ErrorContext | Record<string, any>) => void;
  onPromoteArtifact: (artifact: ConversationArtifact) => Promise<void> | void;
  onDeleteArtifact: (artifact: ConversationArtifact) => Promise<void> | void;
}

export function useArtifacts({ artifacts, sendError, onPromoteArtifact, onDeleteArtifact }: UseArtifactsInputs) {
  const { dioptraFetch } = useDioptraFetch();
  const [downloadingArtifactIds, setDownloadingArtifactIds] = useState<Set<string>>(new Set());
  const [deletingArtifactIds, setDeletingArtifactIds] = useState<Set<string>>(new Set());
  const [collapsedArtifactFolderPaths, setCollapsedArtifactFolderPaths] = useState<Set<string>>(new Set());
  const [artifactAwaitingDeleteConfirmation, setArtifactAwaitingDeleteConfirmation] = useState<ConversationArtifact | null>(null);
  const workspaceArtifacts = useMemo(
    () => artifacts.filter((a) => a.scope !== "conversation"),
    [artifacts],
  );
  const chatExclusiveArtifacts = useMemo(
    () => artifacts.filter((a) => a.scope === "conversation"),
    [artifacts],
  );
  const workspaceArtifactTree = useMemo(
    () => buildArtifactTree(workspaceArtifacts),
    [workspaceArtifacts],
  );
  const chatExclusiveArtifactTree = useMemo(
    () => buildArtifactTree(chatExclusiveArtifacts),
    [chatExclusiveArtifacts],
  );
  const allArtifactsTree = useMemo(() => buildArtifactTree(artifacts), [artifacts]);
  const [promotingArtifactIds, setPromotingArtifactIds] = useState<Set<string>>(new Set());
  const toggleArtifactFolder = (accumulatedFolderPath: string) => {
    setCollapsedArtifactFolderPaths(prev => {
      const next = new Set(prev);
      if (next.has(accumulatedFolderPath)) next.delete(accumulatedFolderPath);
      else next.add(accumulatedFolderPath);
      return next;
    });
  };
  const handleDownloadArtifact = async (artifact: ConversationArtifact) => {
    if (downloadingArtifactIds.has(artifact.id)) return;
    setDownloadingArtifactIds(prev => {
      const next = new Set(prev);
      next.add(artifact.id);
      return next;
    });
    try {
      await downloadConversationArtifact(dioptraFetch, artifact.id);
    } catch (error) {
      console.error('Error downloading artifact:', error);
      sendError(error as Error, {
        component: 'LocalFilesPanel',
        action: 'download_artifact',
        artifactId: artifact.id,
      });
    } finally {
      setDownloadingArtifactIds(prev => {
        const next = new Set(prev);
        next.delete(artifact.id);
        return next;
      });
    }
  };
  const handlePromoteArtifact = async (artifact: ConversationArtifact) => {
    if (!onPromoteArtifact || promotingArtifactIds.has(artifact.id)) return;
    setPromotingArtifactIds(prev => {
      const next = new Set(prev);
      next.add(artifact.id);
      return next;
    });
    try {
      await onPromoteArtifact(artifact);
    } catch (error) {
      console.error('Error promoting artifact:', error);
      sendError(error as Error, {
        component: 'LocalFilesPanel',
        action: 'promote_artifact',
        artifactId: artifact.id,
      });
    } finally {
      setPromotingArtifactIds(prev => {
        const next = new Set(prev);
        next.delete(artifact.id);
        return next;
      });
    }
  };
  const requestArtifactDelete = (artifact: ConversationArtifact) => {
    if (deletingArtifactIds.has(artifact.id)) return;
    setArtifactAwaitingDeleteConfirmation(artifact);
  };
  const handleArtifactDeleteCancel = () => setArtifactAwaitingDeleteConfirmation(null);
  const handleArtifactDeleteConfirm = async () => {
    const artifact = artifactAwaitingDeleteConfirmation;
    if (!artifact || !onDeleteArtifact) {
      setArtifactAwaitingDeleteConfirmation(null);
      return;
    }
    setArtifactAwaitingDeleteConfirmation(null);
    setDeletingArtifactIds(prev => {
      const next = new Set(prev);
      next.add(artifact.id);
      return next;
    });
    try {
      await onDeleteArtifact(artifact);
    } catch (error) {
      console.error('Error deleting artifact:', error);
      sendError(error as Error, {
        component: 'LocalFilesPanel',
        action: 'delete_artifact',
        artifactId: artifact.id,
      });
    } finally {
      setDeletingArtifactIds(prev => {
        const next = new Set(prev);
        next.delete(artifact.id);
        return next;
      });
    }
  };
  return { downloadingArtifactIds, deletingArtifactIds, collapsedArtifactFolderPaths, artifactAwaitingDeleteConfirmation, promotingArtifactIds, workspaceArtifacts, chatExclusiveArtifacts, workspaceArtifactTree, chatExclusiveArtifactTree, allArtifactsTree, toggleArtifactFolder, handleDownloadArtifact, handlePromoteArtifact, requestArtifactDelete, handleArtifactDeleteCancel, handleArtifactDeleteConfirm };
}
